<template>
  <div class="assessment-view-section">
    <div
      v-for="(assessment, assessmentIndex) in assessments"
      :key="assessmentIndex"
      class="assessment-item"
      :data-block-id="assessment.id"
    >
      <div class="assessment-header">
        <div
          class="assessment-title"
          @click="toggleAccordion(assessmentIndex)"
          role="button"
          tabindex="0"
          @keydown.enter="toggleAccordion(assessmentIndex)"
          @keydown.space.prevent="toggleAccordion(assessmentIndex)"
          :aria-expanded="openAssessmentIndex === assessmentIndex"
          :class="{ opened: openAssessmentIndex === assessmentIndex }"
        >
          <div class="title-content">
            <img src="/images/icon/assesment-title.svg" alt="Assessment" />
            <span>{{ assessment.title }}</span>
          </div>
          <div class="arrow-container">
            <span
              class="accordion-arrow"
              :class="{ 'is-open': openAssessmentIndex === assessmentIndex }"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM9 5C9 4.44772 8.55228 4 8 4C7.44771 4 7 4.44772 7 5L7 8.58579L5.70711 7.29289C5.31658 6.90237 4.68342 6.90237 4.29289 7.29289C3.90237 7.68342 3.90237 8.31658 4.29289 8.70711L7.29289 11.7071C7.68342 12.0976 8.31658 12.0976 8.70711 11.7071L11.7071 8.70711C12.0976 8.31658 12.0976 7.68342 11.7071 7.29289C11.3166 6.90237 10.6834 6.90237 10.2929 7.29289L9 8.58579V5Z"
                  fill="white"
                />
              </svg>
            </span>
          </div>
        </div>
      </div>

      <div
        class="assessment-body"
        :class="{ open: openAssessmentIndex === assessmentIndex }"
      >
        <div class="assessment-list">
          <div
            v-for="(item, itemIndex) in assessment.items"
            :key="itemIndex"
            class="assessment-list-item"
          >
            <div class="list-item-content">
              <div class="item-header">
                <img :src="getItemHeaderIcon(itemIndex)" alt="Item" />
                <span class="item-header-text">
                  {{ getItemHeaderText(itemIndex) }}
                </span>
              </div>
              <ul v-if="item.bullets && item.bullets.length" class="item-text">
                <li v-for="(bullet, bIndex) in item.bullets" :key="bIndex">
                  {{ bullet }}
                </li>
              </ul>
              <div v-else class="item-text">
                {{ item.text }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps } from "vue";

defineProps({
  assessments: {
    type: Array,
    default: () => [],
  },
});

const openAssessmentIndex = ref(0); // Keep the first one open by default

const itemHeadersData = [
  {
    text: "Emerging",
    icon: "/images/icon/emerging.svg",
  },
  {
    text: "Developing",
    icon: "/images/icon/developing.svg",
  },
  {
    text: "Proficient",
    icon: "/images/icon/proficient.svg",
  },
];

const toggleAccordion = (index) => {
  openAssessmentIndex.value =
    openAssessmentIndex.value === index ? null : index;
};

const getItemHeaderIcon = (itemIndex) => {
  return itemHeadersData[itemIndex % itemHeadersData.length].icon;
};

const getItemHeaderText = (itemIndex) => {
  return itemHeadersData[itemIndex % itemHeadersData.length].text;
};
</script>

<style scoped>
.assessment-view-section {
  padding: 10px;
}

.assessment-item {
  margin-bottom: 20px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.assessment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 0;
}

.assessment-title {
  flex-grow: 1;
  font-weight: 600;
  padding: 14px 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  font-size: var(--fs-big);
  background-color: var(--green-dark-1, #026843);
  color: white;
  border-radius: 10px;
}

.title-content {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.arrow-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 20px;
}

.accordion-arrow {
  transition: transform 0.3s ease;
  color: white;
}

.accordion-arrow.is-open {
  transform: rotate(180deg);
}

.assessment-body {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.5s cubic-bezier(0, 1, 0, 1);
  opacity: 0;
  background-color: #fff;
  padding: 0;
}

.assessment-body.open {
  max-height: 2000px;
  opacity: 1;
  padding: 15px 0px;
  transition:
    max-height 0.5s ease-in-out,
    opacity 0.3s ease-in,
    padding 0.3s ease;
}

.assessment-list-item {
  background-color: #f2f3fb;
  border-radius: 10.615px;
  padding: 17.692px;
  margin-bottom: 12px;
  transition: all 0.2s ease;
}

.assessment-list-item:last-child {
  margin-bottom: 0;
}

.list-item-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
}

.item-header {
  display: flex;
  align-items: center;
  gap: 14.154px;
  margin-bottom: 0;
}

.item-header img {
  width: 27.728px;
  height: 17.692px;
  object-fit: contain;
}

.item-header-text {
  font-family: "League Spartan", sans-serif;
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
  letter-spacing: 0.03px;
  color: #3b4648;
  white-space: nowrap;
}

.item-text {
  font-family: "League Spartan", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.035px;
  color: #3b4648;
  padding-left: 0;
  width: 100%;
}

ul.item-text {
  margin: 0;
  padding-left: 1.25rem;
}

ul.item-text li {
  margin-bottom: 4px;
}

ul.item-text li:last-child {
  margin-bottom: 0;
}
</style>
